// ═══════════════════════════════════════════════════════════════
// MainchatContainer.js - 채널 목록 통합 버전
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Box, Drawer, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChatIcon from '@mui/icons-material/Chat';

import MainchatTemplate from '../../components/mainchat/MainchatTemplate';
import ChatChannelList from '../../components/mainchat/ChatChannelList';
import CreateChannelDialog from '../../components/mainchat/CreateChannelDialog';
import ChannelSettingsDialog from '../../components/mainchat/ChannelSettingsDialog';
import useStomp from '../../hooks/useStomp';
import { fetchChatHistory, getParticipants, removeParticipant, transferOwnership } from '../../lib/api/chatApi';
import { requestNotificationPermission, showMentionWindowNotification } from '../../utils/notificationUtils';
import client from '../../lib/api/client';

// 연결 상태 상수
const CONNECTION_STATUS = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  RECONNECTING: 'reconnecting',
  ERROR: 'error'
};

const PMS_BOT_CHANNEL_NAME = '내 채팅 (KMES BOT)';

// 채팅 이력은 최근 1주일만 먼저 불러오고, 그 이전은 사용자가 요청할 때 1주일씩 이어 받는다.
const HISTORY_WINDOW_DAYS = 7;
// 한 번에 가져올 상한. 대화가 몰린 주에도 응답이 과도해지지 않게 막는다(서버도 동일 상한을 둔다).
const HISTORY_PAGE_LIMIT = 300;
// 메시지가 없는 주가 이어지면 사용자는 "더 보기"를 몇 번 눌러야 할지 알 수 없다.
// 빈 구간은 이만큼까지 자동으로 거슬러 올라가며 찾는다.
const HISTORY_MAX_EMPTY_WEEKS = 8;

const toSqlDateTime = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} `
    + `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const weeksAgo = (weeks) => {
  const d = new Date();
  d.setDate(d.getDate() - weeks * HISTORY_WINDOW_DAYS);
  return d;
};

const createClientTempId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `tmp-${crypto.randomUUID()}`;
  }

  return `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const PERSONAL_ROOM_ACCESS_DENIED_TEXT = '채팅방 접근 권한이 없습니다.';

const extractBotMessageText = (message) => {
  if (!message || typeof message.content !== 'string') {
    return '';
  }

  const raw = message.content.trim();
  if (!raw) {
    return '';
  }

  if (!raw.startsWith('{')) {
    return raw;
  }

  try {
    const parsed = JSON.parse(raw);
    return parsed?.message || raw;
  } catch {
    return raw;
  }
};

const isBotMessage = (message) =>
  message?.crtUsr === 'BOT' ||
  message?.usrNm === 'KMES BOT' ||
  message?.crtNm === 'KMES BOT' ||
  // 2026-10-15 이름 변경 전(PMS BOT)에 저장된 메시지도 봇으로 본다
  message?.usrNm === 'PMS BOT' ||
  message?.crtNm === 'PMS BOT' ||
  message?.crtUsr === 'BOT_PMS_SYSTEM' ||
  message?.isBot ||
  message?.chatTyp === 'bot_response';

const isIgnorablePersonalRoomAccessError = (message, currentUserUuid) => {
  if (!currentUserUuid || !message || message.chatRoomUuid !== currentUserUuid || !isBotMessage(message)) {
    return false;
  }

  return extractBotMessageText(message).includes(PERSONAL_ROOM_ACCESS_DENIED_TEXT);
};

const isPmsBotChannelName = (channelName) =>
  typeof channelName === 'string' && (channelName.includes('KMES BOT') || channelName.includes('PMS BOT'));

const shouldRecoverPersonalBotRoom = ({ roomUuid, channelName, currentUserUuid, participants = [] }) => {
  if (!currentUserUuid || !roomUuid || roomUuid === currentUserUuid) {
    return false;
  }

  if (isPmsBotChannelName(channelName)) {
    return true;
  }

  return participants.length === 1 && participants[0]?.usrUuid === roomUuid;
};

const mergeMessagesByUuid = (historyMessages = [], liveMessages = []) => {
  const seen = new Set();
  const merged = [];

  const append = (message) => {
    if (!message) return;
    const key = message.messageUuid || `${message.crtDt || message.regDt || ''}-${message.crtUsr || ''}-${message.content || ''}`;
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(message);
  };

  historyMessages.forEach(append);
  liveMessages.forEach(append);

  return merged;
};

const upsertMessage = (messages = [], nextMessage) => {
  if (!nextMessage) return messages;

  const existingIndex = messages.findIndex(message =>
    (nextMessage.clientTempId && message.clientTempId === nextMessage.clientTempId) ||
    (nextMessage.messageUuid && message.messageUuid === nextMessage.messageUuid)
  );

  if (existingIndex === -1) {
    return [...messages, nextMessage];
  }

  const updated = [...messages];
  updated[existingIndex] = {
    ...updated[existingIndex],
    ...nextMessage,
    isPending: nextMessage.isPending ?? false,
  };
  return updated;
};

const MainchatContainer = () => {
  // ===== 모든 State 먼저 정의 =====
  const [messages, setMessages] = useState([]);
  // 지금까지 불러온 이력의 시작 지점(이보다 과거를 더 받을 때 커서로 쓴다)
  const [historySince, setHistorySince] = useState(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasOlderHistory, setHasOlderHistory] = useState(true);
  // 채팅 메시지 푸시 알림을 탭하면 /mainchat?room=<uuid> 로 들어온다 - 그 방을 최우선으로 연다.
  const [searchParams] = useSearchParams();

  // sessionStorage에서 마지막 채팅방 복원 (페이지 이동 후 돌아올 때 유지)
  const [chatRoomUuid, setChatRoomUuid] = useState(
    () => {
      const roomFromQuery = new URLSearchParams(window.location.search).get('room');
      if (roomFromQuery) {
        return roomFromQuery;
      }
      const savedRoomUuid = sessionStorage.getItem('lastChatRoomUuid');
      if (!savedRoomUuid || savedRoomUuid === 'null' || savedRoomUuid === 'undefined') {
        return undefined;
      }
      return savedRoomUuid;
    }
  );
  const [connectionStatus, setConnectionStatus] = useState(CONNECTION_STATUS.CONNECTING);
  const [connectionDebugInfo, setConnectionDebugInfo] = useState({
    lastConnected: null,
    lastDisconnected: null,
    reconnectAttempts: 0,
    stompClientStatus: 'unknown'
  });
  const [isTyping, setIsTyping] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadingUuid, setDownloadingUuid] = useState(null);
  const [debugPanelOpen, setDebugPanelOpen] = useState(true);

  // 멤버 관련 state
  const [participants, setParticipants] = useState([]);
  const [onlineUuids, setOnlineUuids] = useState([]);
  const [myRole, setMyRole] = useState('MEMBER');
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);

  // 채널 관련 State
  const [showChannelList] = useState(true);
  const [mobileChannelListOpen, setMobileChannelListOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [selectedChannelForSettings, setSelectedChannelForSettings] = useState(null);
  const [channelListKey, setChannelListKey] = useState(0); // 채널 목록 새로고침용

  // ===== Redux & Hooks =====
  const { user } = useSelector(({ auth }) => ({ user: auth.user }));
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ===== 토큰 처리 =====
  const storedToken = localStorage.getItem('access_token');
  const token = storedToken
    ? (() => {
        try {
          return JSON.parse(storedToken);
        } catch {
          return storedToken;
        }
      })()
    : null;

  const currentRoomRef = useRef(chatRoomUuid);
  const recoveredRoomRef = useRef(null);
  const botTypingTimeoutRef = useRef(null);

  // 2026-10-16: axios 인터셉터가 액세스 토큰을 리프레시하면 localStorage만 바뀐다 — React는
  // localStorage를 관찰하지 않으므로, 이 컴포넌트가 리렌더되도록 강제로 트리거해 위 `token`
  // 상수가 최신값으로 다시 계산되게 한다. token이 바뀌면 useStomp의 effect 의존성이 바뀌어
  // STOMP 클라이언트가 새 토큰으로 재연결된다(client.js의 'kmes:token-refreshed' 참고).
  const [, forceRerenderOnTokenRefresh] = useState(0);
  useEffect(() => {
    const handler = () => forceRerenderOnTokenRefresh((n) => n + 1);
    window.addEventListener('kmes:token-refreshed', handler);
    return () => window.removeEventListener('kmes:token-refreshed', handler);
  }, []);

  // ===== 채널 관련 핸들러 =====
  const [selectedChannelName, setSelectedChannelName] = useState(
    () => {
      const savedChannelName = sessionStorage.getItem('lastChannelName');
      if (!savedChannelName || savedChannelName === 'null' || savedChannelName === 'undefined') {
        return null;
      }
      return savedChannelName;
    }
  );

  const effectiveChatRoomUuid = useMemo(() => {
    if (!user?.usrUuid) {
      return chatRoomUuid;
    }

    if (user?.memberUuid && chatRoomUuid === user.memberUuid) {
      return user.usrUuid;
    }

    if (shouldRecoverPersonalBotRoom({
      roomUuid: chatRoomUuid,
      channelName: selectedChannelName,
      currentUserUuid: user.usrUuid,
      participants,
    })) {
      return user.usrUuid;
    }

    return chatRoomUuid;
  }, [chatRoomUuid, participants, selectedChannelName, user?.memberUuid, user?.usrUuid]);

  useEffect(() => {
    if (!user?.usrUuid || !user?.memberUuid || user.memberUuid === user.usrUuid) {
      return;
    }

    if (chatRoomUuid !== user.memberUuid) {
      return;
    }

    setChatRoomUuid(user.usrUuid);
    sessionStorage.setItem('lastChatRoomUuid', user.usrUuid);
    if (!selectedChannelName || isPmsBotChannelName(selectedChannelName)) {
      setSelectedChannelName(PMS_BOT_CHANNEL_NAME);
      sessionStorage.setItem('lastChannelName', PMS_BOT_CHANNEL_NAME);
    }
  }, [chatRoomUuid, selectedChannelName, user?.memberUuid, user?.usrUuid]);

  useEffect(() => {
    currentRoomRef.current = effectiveChatRoomUuid;
  }, [effectiveChatRoomUuid]);

  // ===== 모든 콜백 함수들 먼저 정의 =====
  const logConnectionStatus = useCallback((status, details = '') => {
    const timestamp = new Date().toLocaleTimeString();
    console.log(`🔗 [${timestamp}] 연결 상태 변경: ${status} ${details}`);
    
    setConnectionStatus(status);
    setConnectionDebugInfo(prev => ({
      ...prev,
      lastStatusChange: timestamp,
      [`last${status.charAt(0).toUpperCase() + status.slice(1)}`]: timestamp,
      ...(status === 'reconnecting' && { reconnectAttempts: prev.reconnectAttempts + 1 })
    }));
  }, []);

  const clearBotTypingTimeout = useCallback(() => {
    if (!botTypingTimeoutRef.current) {
      return;
    }

    clearTimeout(botTypingTimeoutRef.current);
    botTypingTimeoutRef.current = null;
  }, []);

  const showBotTyping = useCallback(() => {
    clearBotTypingTimeout();
    setIsTyping(true);
    botTypingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      botTypingTimeoutRef.current = null;
    }, 45000);
  }, [clearBotTypingTimeout]);

  const handleIncoming = useCallback((message) => {
    console.log('📨 수신된 메시지:', message);

    if (
      message?.chatRoomUuid &&
      currentRoomRef.current &&
      message.chatRoomUuid !== currentRoomRef.current
    ) {
      console.log('↩️ 현재 채널이 아니어서 메시지 무시:', message.chatRoomUuid);
      return;
    }

    if (isIgnorablePersonalRoomAccessError(message, user?.usrUuid)) {
      console.warn('⚠️ 개인 PMS BOT 방의 잘못된 접근 거부 메시지 무시');
      return;
    }

    // ★ 온라인 사용자 목록 업데이트 (백엔드 ONLINE_USERS 브로드캐스트)
    if (message.type === 'ONLINE_USERS' && Array.isArray(message.onlineUuids)) {
      setOnlineUuids(message.onlineUuids);
      return;
    }

    // WebSocket 실시간 메시지도 replyToMessage 객체로 변환
    // 1순위: 서버가 내려준 replyContent, 2순위: 프론트 캐시
    if (message.replyToUuid && !message.replyToMessage) {
      const cachedReply = replyTargetCacheRef.current[message.replyToUuid];
      message = {
        ...message,
        replyToMessage: {
          messageUuid: message.replyToUuid,
          content: message.replyContent || cachedReply?.content || cachedReply?.text,
          usrNm: message.replyUsrNm || cachedReply?.usrNm || cachedReply?.crtNm,
          crtUsr: message.replyCrtUsr || cachedReply?.crtUsr,
        },
      };
    }

    if (!document.hasFocus() || !isAtBottom) {
      setUnreadCount(prev => prev + 1);
    }
    
    if (isBotMessage(message)) {
      clearBotTypingTimeout();
      setIsTyping(false);
    }

    setMessages(prev => upsertMessage(prev, message));
  }, [clearBotTypingTimeout, isAtBottom, user?.usrUuid]);

  const handleFileSelect = useCallback(async (file) => {
    if (!file || uploadingFile) return;

    if (!effectiveChatRoomUuid || !user?.usrUuid) {
      enqueueSnackbar('채팅방이 준비된 후 다시 업로드해주세요.', { variant: 'warning' });
      return;
    }
    
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      enqueueSnackbar('파일 크기가 50MB를 초과합니다.', { variant: 'error' });
      return;
    }

    setUploadingFile(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await client.post(
        '/api/v1/messages/info/file/upload',
        formData,
        {
          params: {
            chatRoomUuid: effectiveChatRoomUuid,
            sender: user.usrUuid,
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = progressEvent.total
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 0;
            setUploadProgress(percentCompleted);
          }
        }
      );

      // 모바일 환경에서 WebSocket 브로드캐스트가 늦거나 누락되어도
      // 업로드 응답의 파일 메시지와 연결 액션을 즉시 표시한다. 동일 메시지가
      // WebSocket으로 도착하면 upsertMessage가 messageUuid 기준으로 중복을 제거한다.
      const uploadedMessage = response?.data?.fileMessage;
      if (uploadedMessage) {
        handleIncoming(uploadedMessage);
      }
      const actionMessage = response?.data?.actionMessage;
      if (actionMessage) {
        handleIncoming(actionMessage);
      }
      
      enqueueSnackbar('파일이 업로드되었습니다.', { variant: 'success' });
    } catch (error) {
      console.error('파일 업로드 실패:', error);
      enqueueSnackbar('파일 업로드에 실패했습니다.', { variant: 'error' });
    } finally {
      setUploadingFile(false);
      setUploadProgress(0);
    }
  }, [uploadingFile, effectiveChatRoomUuid, user?.usrUuid, enqueueSnackbar, handleIncoming]);

  const handleFileDownload = useCallback(async (fileUuid, fileName) => {
    if (!fileUuid || !fileName) return;
    
    setDownloadingUuid(fileUuid);
    try {
      const res = await client.get(
        `/api/v1/messages/info/file/download/${fileUuid}`,
        { 
          responseType: 'blob',
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar('파일 다운로드가 완료되었습니다.', { variant: 'success' });
    } catch (err) {
      console.error('파일 다운로드 실패:', err);
      enqueueSnackbar('파일 다운로드에 실패했습니다.', { variant: 'error' });
    } finally {
      setDownloadingUuid(null);
    }
  }, [token, enqueueSnackbar]);

  const handleScrollStateChange = useCallback((newIsAtBottom) => {
    setIsAtBottom(newIsAtBottom);
    if (newIsAtBottom) {
      setUnreadCount(0);
    }
  }, []);

  const recoverPersonalBotRoom = useCallback((reason = '') => {
    if (!user?.usrUuid || !chatRoomUuid || chatRoomUuid === user.usrUuid) {
      return;
    }

    recoveredRoomRef.current = chatRoomUuid;
    console.warn('⚠️ 잘못된 PMS BOT 개인방 복구:', { from: chatRoomUuid, to: user.usrUuid, reason });
    setChatRoomUuid(user.usrUuid);
    setSelectedChannelName(PMS_BOT_CHANNEL_NAME);
    setMessages([]);
    setParticipants([]);
    setOnlineUuids([]);
    sessionStorage.setItem('lastChatRoomUuid', user.usrUuid);
    sessionStorage.setItem('lastChannelName', PMS_BOT_CHANNEL_NAME);
    logConnectionStatus(CONNECTION_STATUS.CONNECTING, '- 개인 채팅방 복구');
    enqueueSnackbar('잘못된 KMES BOT 개인 채팅방 연결을 복구했습니다.', { variant: 'warning' });
  }, [chatRoomUuid, enqueueSnackbar, logConnectionStatus, user?.usrUuid]);

  // 이전 1주일치를 이어서 불러온다.
  // 활동이 없던 주에는 아무것도 안 늘어나 사용자가 몇 번을 눌러야 할지 알 수 없으므로,
  // 메시지가 나올 때까지(최대 HISTORY_MAX_EMPTY_WEEKS 주) 자동으로 거슬러 올라간다.
  const handleLoadOlderHistory = useCallback(async () => {
    if (!chatRoomUuid || loadingOlder || !hasOlderHistory || !historySince) return;

    setLoadingOlder(true);
    try {
      let cursor = historySince;
      for (let step = 0; step < HISTORY_MAX_EMPTY_WEEKS; step += 1) {
        const nextSince = toSqlDateTime(
          new Date(new Date(cursor.replace(' ', 'T')).getTime() - HISTORY_WINDOW_DAYS * 86400000),
        );

        // eslint-disable-next-line no-await-in-loop
        const data = await fetchChatHistory(chatRoomUuid, null, {
          since: nextSince,
          before: cursor,
          limit: HISTORY_PAGE_LIMIT,
        });
        const rawList = Array.isArray(data) ? data : (data?.list || data?.data || []);
        cursor = nextSince;

        if (rawList.length > 0) {
          const msgList = rawList.map((msg) => ({
            ...msg,
            replyToMessage: msg.replyToUuid ? {
              messageUuid: msg.replyToUuid,
              content: msg.replyContent,
              usrNm: msg.replyUsrNm,
              crtUsr: msg.replyCrtUsr,
            } : null,
          })).filter((msg) => !isIgnorablePersonalRoomAccessError(msg, user?.usrUuid));

          setMessages((prev) => mergeMessagesByUuid(msgList, prev));
          setHistorySince(nextSince);
          return;
        }
      }

      // 정해둔 만큼 거슬러 올라가도 메시지가 없으면 더 보여줄 게 없다고 본다.
      setHistorySince(cursor);
      setHasOlderHistory(false);
    } catch (err) {
      console.error('❌ 이전 채팅 내역 로드 실패:', err);
      enqueueSnackbar('이전 대화를 불러오지 못했습니다.', { variant: 'error' });
    } finally {
      setLoadingOlder(false);
    }
  }, [chatRoomUuid, loadingOlder, hasOlderHistory, historySince, user?.usrUuid, enqueueSnackbar]);

  const handleSelectRoom = useCallback((roomUuid, roomName) => {
    if (!roomUuid) {
      setChatRoomUuid(undefined);
      setSelectedChannelName(null);
      sessionStorage.removeItem('lastChatRoomUuid');
      sessionStorage.removeItem('lastChannelName');
      setMessages([]);
      setParticipants([]);
      setOnlineUuids([]);
      setUnreadCount(0);
      logConnectionStatus(CONNECTION_STATUS.DISCONNECTED, '- 채널 선택 해제');
      return;
    }

    console.log('🏠 채널 선택:', roomUuid);
    setChatRoomUuid(roomUuid);
    setSelectedChannelName(roomName || null);
    // 마지막 선택 채널 sessionStorage에 저장 (페이지 이동 후 복원용)
    sessionStorage.setItem('lastChatRoomUuid', roomUuid);
    sessionStorage.setItem('lastChannelName', roomName || '');
    logConnectionStatus(CONNECTION_STATUS.CONNECTING, '- 채널 변경');
  }, [logConnectionStatus]);

  // 이미 /mainchat 화면이 떠 있는 상태에서 채팅 푸시 알림을 또 탭한 경우(쿼리 파라미터만 바뀜) -
  // 최초 마운트는 위 useState 초기값으로 처리되므로, 여기서는 "마운트된 채로 room만 바뀐" 경우만 본다.
  useEffect(() => {
    const roomFromQuery = searchParams.get('room');
    if (roomFromQuery && roomFromQuery !== chatRoomUuid) {
      handleSelectRoom(roomFromQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleCreateRoom = useCallback(() => {
    setMobileChannelListOpen(false);
    setCreateDialogOpen(true);
  }, []);

  const handleOpenSettings = useCallback((room) => {
    setMobileChannelListOpen(false);
    setSelectedChannelForSettings(room);
    setSettingsDialogOpen(true);
  }, []);

  const handleChannelCreated = useCallback((newChannel) => {
    setChannelListKey(prev => prev + 1); // 채널 목록 새로고침
    setChatRoomUuid(newChannel.chatRoomUuid); // 새로 만든 채널로 이동
    setSelectedChannelName(newChannel.name || null);
    sessionStorage.setItem('lastChatRoomUuid', newChannel.chatRoomUuid);
    sessionStorage.setItem('lastChannelName', newChannel.name || '');
    logConnectionStatus(CONNECTION_STATUS.CONNECTING, '- 새 채널 생성');
    enqueueSnackbar(`'${newChannel.name}' 채널이 생성되었습니다.`, { variant: 'success' });
  }, [enqueueSnackbar, logConnectionStatus]);

  const handleChannelUpdated = useCallback(() => {
    setChannelListKey(prev => prev + 1); // 채널 목록 새로고침
  }, []);

  // ===== 멘션/답글 알림 핸들러 =====
  const handleMentionNotification = useCallback((notification) => {
    const isReply = notification.type === 'REPLY';

    // 1) 앱 내 스낵바 - 답글이면 green success, 멘션이면 blue info
    const snackbarMsg = isReply
      ? `💬 ${notification.fromUserNm || '누군가'}님이 내 메시지에 답글을 달았어요: "${notification.messagePreview || ''}"`
      : `🔔 ${notification.fromUserNm || '누군가'}님이 회원님을 멘션했습니다: "${notification.messagePreview || ''}"` ;

    enqueueSnackbar(snackbarMsg, {
      variant: isReply ? 'success' : 'info',
      autoHideDuration: 5000,
      anchorOrigin: { vertical: 'top', horizontal: 'right' },
    });

    // 2) 브라우저 윈도우 알림 (탭 비활성 시)
    showMentionWindowNotification(
      {
        ...notification,
        fromUserNm: isReply
          ? `💬 ${notification.fromUserNm || '누군가'} (답글)`
          : notification.fromUserNm,
      },
      () => {
        if (notification.chatRoomUuid) {
          setChatRoomUuid(notification.chatRoomUuid);
          setSelectedChannelName(null);
          sessionStorage.setItem('lastChatRoomUuid', notification.chatRoomUuid);
          sessionStorage.removeItem('lastChannelName');
          logConnectionStatus(CONNECTION_STATUS.CONNECTING, '- 알림에서 채널 이동');
        }
      }
    );
  }, [enqueueSnackbar, logConnectionStatus]);

  const handleStompConnectionChange = useCallback((status) => {
    switch (status) {
      case 'connecting':
        logConnectionStatus(CONNECTION_STATUS.CONNECTING, '- STOMP 연결 시도');
        break;
      case 'connected':
        logConnectionStatus(CONNECTION_STATUS.CONNECTED, '- STOMP 연결 성공');
        break;
      case 'error':
        logConnectionStatus(CONNECTION_STATUS.ERROR, '- STOMP 연결 오류');
        break;
      case 'disconnected':
        logConnectionStatus(CONNECTION_STATUS.DISCONNECTED, '- STOMP 연결 종료');
        break;
      default:
        break;
    }
  }, [logConnectionStatus]);

  // ===== STOMP 클라이언트 생성 =====
  const stompClient = useStomp({
    brokerURL: '/api/v1/messages/ws',
    token,
    roomId: effectiveChatRoomUuid,
    usrUuid: user?.usrUuid,
    onMessage: handleIncoming,
    onMentionNotification: handleMentionNotification,
    onConnectionChange: handleStompConnectionChange,
  });

  // ===== 메시지 수정 =====
  const handleEditMessage = useCallback(async (messageUuid, newContent) => {
    try {
      await client.put(`/api/v1/messages/info/${messageUuid}`, { content: newContent });
      setMessages(prev => prev.map(m =>
        m.messageUuid === messageUuid ? { ...m, content: newContent, isEdited: true } : m
      ));
      enqueueSnackbar('메시지가 수정되었습니다.', { variant: 'success' });
    } catch (err) {
      console.error('메시지 수정 실패:', err);
      enqueueSnackbar('메시지 수정에 실패했습니다.', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  // ===== 메시지 삭제 =====
  const handleDeleteMessage = useCallback(async (messageUuid) => {
    try {
      await client.delete(`/api/v1/messages/info/${messageUuid}`);
      setMessages(prev => prev.filter(m => m.messageUuid !== messageUuid));
      enqueueSnackbar('메시지가 삭제되었습니다.', { variant: 'success' });
    } catch (err) {
      console.error('메시지 삭제 실패:', err);
      enqueueSnackbar('메시지 삭제에 실패했습니다.', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  // ===== replyTarget 캐시: 전송 시 원문 내용을 프론트에서 직접 보관 =====
  const replyTargetCacheRef = useRef({});

  // ===== 실제 메시지 전송 함수 (stompClient 사용) =====
  const actualSendMessage = useCallback((content, mentionUuids, replyToUuid, replyTarget) => {
    if (!stompClient?.connected || !content.trim()) {
      console.log('❌ 메시지 전송 실패 - STOMP 연결 없음');
      enqueueSnackbar('서버 연결이 완료된 후 다시 전송해주세요.', { variant: 'warning' });
      return;
    }

    try {
      const clientTempId = createClientTempId();
      const now = new Date();
      const nowIso = now.toISOString();
      const payload = {
        chatRoomUuid: effectiveChatRoomUuid,
        usrUuid: user.usrUuid,
        crtUsr: user.usrUuid,
        // usrNm: 이름, policyNm: 직책 — "강범석 대표이사" 형태로 표시
        crtNm: [user.usrNm, user.policyNm].filter(Boolean).join(' ') || user.usrNm || '사용자',
        usrNm: user.usrNm || '사용자',
        content,
        chatTyp: 'text',
        timestamp: nowIso,
        clientTempId,
        ...(mentionUuids && { mentions: mentionUuids }),
        ...(replyToUuid && { replyToUuid }),
      };

      const optimisticMessage = {
        ...payload,
        messageUuid: clientTempId,
        clientTempId,
        chatRoomUuid: effectiveChatRoomUuid,
        crtDt: nowIso.slice(0, 19).replace('T', ' '),
        regDt: nowIso,
        emoticon: user.profileImg,
        isPending: true,
        replyToMessage: replyToUuid ? {
          messageUuid: replyToUuid,
          content: replyTarget?.content || replyTarget?.text,
          usrNm: replyTarget?.usrNm || replyTarget?.crtNm,
          crtUsr: replyTarget?.crtUsr,
        } : null,
      };
      
      // replyTarget 캐시에 저장 → handleIncoming에서 브로드캐스트 수신 시 원문 복원
      if (replyToUuid && replyTarget) {
        replyTargetCacheRef.current[replyToUuid] = replyTarget;
      }

      setMessages(prev => upsertMessage(prev, optimisticMessage));

      stompClient.publish({
        destination: '/app/chat.sendMessage',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });

      if (effectiveChatRoomUuid === user.usrUuid || isPmsBotChannelName(selectedChannelName)) {
        showBotTyping();
      }

      setMessages(prev => prev.map(message =>
        message.clientTempId === clientTempId
          ? { ...message, isPending: false }
          : message
      ));
      
      console.log('✅ 메시지 전송 성공');
    } catch (error) {
      console.error('❌ 메시지 전송 실패:', error);
      clearBotTypingTimeout();
      setIsTyping(false);
      enqueueSnackbar('메시지 전송에 실패했습니다.', { variant: 'error' });
    }
  }, [stompClient, effectiveChatRoomUuid, user, token, selectedChannelName, showBotTyping, clearBotTypingTimeout, enqueueSnackbar]);

  // ===== 연결 상태 계산 =====
  const isConnected = useMemo(() => {
    if (connectionStatus === CONNECTION_STATUS.CONNECTED) return true;
    if (stompClient?.connected) return true;
    return false;
  }, [connectionStatus, stompClient?.connected]);

  // ===== useEffect들 =====

  // 앱 로드 시 브라우저 알림 권한 요청
  useEffect(() => {
    requestNotificationPermission();
  }, []);

  useEffect(() => () => {
    clearBotTypingTimeout();
  }, [clearBotTypingTimeout]);

  // 기본 채팅방 설정: sessionStorage에 저장된 UUID가 없을 때만 개인 채팅방으로 fallback
  useEffect(() => {
    if (!chatRoomUuid && user?.usrUuid) {
      console.log('🏠 기본 채팅방 UUID 설정 (fallback):', user.usrUuid);
      setChatRoomUuid(user.usrUuid);
      sessionStorage.setItem('lastChatRoomUuid', user.usrUuid);
      sessionStorage.setItem('lastChannelName', PMS_BOT_CHANNEL_NAME);
      logConnectionStatus(CONNECTION_STATUS.CONNECTING, '- 채팅방 준비 중');
    }
  }, [user?.usrUuid, chatRoomUuid, logConnectionStatus]);

  useEffect(() => {
    if (!user?.usrUuid || !chatRoomUuid) {
      return;
    }

    if (!shouldRecoverPersonalBotRoom({
      roomUuid: chatRoomUuid,
      channelName: selectedChannelName,
      currentUserUuid: user.usrUuid,
      participants,
    })) {
      return;
    }

    if (recoveredRoomRef.current === chatRoomUuid) {
      return;
    }

    recoverPersonalBotRoom('stale-personal-room');
  }, [chatRoomUuid, participants, recoverPersonalBotRoom, selectedChannelName, user?.usrUuid]);

  // 채팅 내역 로드: chatRoomUuid 변경 시 항상 최신 내역 로드
  useEffect(() => {
    if (!effectiveChatRoomUuid) return;

    let cancelled = false; // 채널 전환 시 이전 fetch 응답 무시 (레이스 컨디션 방지)

    console.log('📚 채팅 내역 로드 시작:', effectiveChatRoomUuid);
    clearBotTypingTimeout();
    setIsTyping(false);
    setMessages([]); // 이전 채널 메시지 즉시 제거
    const initialSince = toSqlDateTime(weeksAgo(1));
    setHistorySince(initialSince);
    setHasOlderHistory(true);
    fetchChatHistory(effectiveChatRoomUuid, token, { since: initialSince, limit: HISTORY_PAGE_LIMIT })
      .then(data => {
        if (cancelled) return; // 이미 다른 채널로 전환됐으면 무시
        // API 응답이 배열인지, 아니면 { list: [...] } 래퍼인지 처리
        const rawList = Array.isArray(data) ? data : (data?.list || data?.data || []);
        // replyContent/replyUsrNm → replyToMessage 객체로 변환
        const msgList = rawList.map(msg => ({
          ...msg,
          replyToMessage: msg.replyToUuid ? {
            messageUuid: msg.replyToUuid,
            content: msg.replyContent,
            usrNm: msg.replyUsrNm,
            crtUsr: msg.replyCrtUsr,
          } : null,
        })).filter(msg => !isIgnorablePersonalRoomAccessError(msg, user?.usrUuid));
        setMessages(prev => mergeMessagesByUuid(msgList, prev));
        console.log('📚 채팅 내역 로드 완료:', msgList.length, '개 메시지');
      })
      .catch(err => {
        if (cancelled) return;
        console.error('❌ 채팅 내역 로드 실패:', err);
        setMessages([]);
      });

    return () => { cancelled = true; }; // cleanup: 채널 전환 시 이전 fetch 무효화
  }, [clearBotTypingTimeout, effectiveChatRoomUuid, token, user?.usrUuid]);

  // 채팅방 참여자 목록 로드 + myRole 추출
  const reloadParticipants = useCallback(async (roomUuid) => {
    if (!roomUuid) return;
    try {
      const data = await getParticipants(roomUuid);
      setParticipants(data);
      // 내 역할 추출
      const me = data.find(p => p.usrUuid === user?.usrUuid);
      setMyRole(me?.ruleTyp || 'MEMBER');
      // 온라인 상태: STOMP 연결된 사용자는 온라인 처리 (WebSocket 세션이 없으면 현재 사용자만)
      if (stompClient?.connected) {
        setOnlineUuids([user?.usrUuid].filter(Boolean));
      }
    } catch (err) {
      console.error('❌ 참여자 목록 로드 실패:', err);
      setParticipants([]);
    }
  }, [user?.usrUuid, stompClient?.connected]);

  useEffect(() => {
    reloadParticipants(effectiveChatRoomUuid);
  }, [effectiveChatRoomUuid, reloadParticipants]);

  // 멤버 추가 핸들러
  const handleAddMember = useCallback(() => {
    setAddMemberDialogOpen(true);
  }, []);

  // OWNER 이양 핸들러
  const handleTransferOwner = useCallback(async (participant) => {
    if (!window.confirm(`'${participant.usrNm}' 님에게 소유자 권한을 이양하시겠습니까?\n\n이양 후 회원님은 일반 멤버가 됩니다.`)) return;
    try {
      await transferOwnership(effectiveChatRoomUuid, participant.usrUuid);
      enqueueSnackbar(`'${participant.usrNm}' 님에게 소유자를 이양했습니다.`, { variant: 'success' });
      reloadParticipants(effectiveChatRoomUuid);
    } catch (err) {
      console.error('OWNER 이양 실패:', err);
      enqueueSnackbar('OWNER 이양에 실패했습니다.', { variant: 'error' });
    }
  }, [effectiveChatRoomUuid, enqueueSnackbar, reloadParticipants]);

  // 멤버 추방 핸들러
  const handleRemoveMember = useCallback(async (participant) => {
    if (!window.confirm(`'${participant.usrNm}' 님을 채널에서 추방하시겠습니까?`)) return;
    try {
      await removeParticipant(effectiveChatRoomUuid, participant.usrUuid);
      enqueueSnackbar(`'${participant.usrNm}' 님을 추방했습니다.`, { variant: 'success' });
      reloadParticipants(effectiveChatRoomUuid);
    } catch (err) {
      console.error('멤버 추방 실패:', err);
      enqueueSnackbar('멤버 추방에 실패했습니다.', { variant: 'error' });
    }
  }, [effectiveChatRoomUuid, enqueueSnackbar, reloadParticipants]);

  // STOMP 클라이언트 상태 모니터링
  useEffect(() => {
    if (!stompClient) {
      logConnectionStatus(CONNECTION_STATUS.CONNECTING, '- STOMP 클라이언트 초기화 중');
      return;
    }

    const checkInitialConnection = () => {
      if (stompClient.connected) {
        logConnectionStatus(CONNECTION_STATUS.CONNECTED, '- 초기 연결 확인');
      }
    };

    const initialCheck = setTimeout(checkInitialConnection, 100);

    const statusCheckInterval = setInterval(() => {
      const wasConnected = connectionStatus === CONNECTION_STATUS.CONNECTED;
      const isNowConnected = stompClient.connected;
      
      setConnectionDebugInfo(prev => ({
        ...prev,
        stompClientStatus: isNowConnected ? 'connected' : 'disconnected',
        stompClientActive: stompClient.active
      }));

      if (isNowConnected && !wasConnected) {
        logConnectionStatus(CONNECTION_STATUS.CONNECTED, '- 연결 감지');
      } else if (!isNowConnected && wasConnected) {
        logConnectionStatus(CONNECTION_STATUS.DISCONNECTED, '- 연결 해제 감지');
      }
    }, 3000);

    return () => {
      clearTimeout(initialCheck);
      clearInterval(statusCheckInterval);
    };
  }, [stompClient, connectionStatus, logConnectionStatus]);

  // 페이지 포커스 시 읽지 않은 메시지 초기화
  useEffect(() => {
    const handleFocus = () => {
      if (isAtBottom) {
        setUnreadCount(0);
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [isAtBottom]);

  return (
    <Box sx={{ 
      display: 'flex', 
      width: '100%',
      flex: 1,
      minWidth: 0,
      height: '100%',
      minHeight: 0,
      overflow: 'hidden',
    }}>
      {/* 메인 채팅 영역 */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
        {effectiveChatRoomUuid ? (
          <MainchatTemplate
            messages={messages}
            onSend={actualSendMessage}
            onFileSelect={handleFileSelect}
            currentUser={user?.usrUuid}
            connectionStatus={connectionStatus}
            isConnected={isConnected}
            isTyping={isTyping}
            unreadCount={unreadCount}
            isAtBottom={isAtBottom}
            uploadingFile={uploadingFile}
            uploadProgress={uploadProgress}
            downloadingUuid={downloadingUuid}
            onFileDownload={handleFileDownload}
            onBotMessage={handleIncoming}
            onScrollStateChange={handleScrollStateChange}
            onLoadOlderHistory={handleLoadOlderHistory}
            loadingOlderHistory={loadingOlder}
            hasOlderHistory={hasOlderHistory}
            userInfo={user}
            chatRoomUuid={effectiveChatRoomUuid}
            channelName={selectedChannelName || (effectiveChatRoomUuid === user?.usrUuid ? PMS_BOT_CHANNEL_NAME : '채널')}
            participants={participants}
            onlineUuids={onlineUuids}
            myRole={myRole}
            onAddMember={handleAddMember}
            onRemoveMember={handleRemoveMember}
            onTransferOwner={handleTransferOwner}
            onOpenChannelList={isMobile ? () => setMobileChannelListOpen(true) : undefined}
            onEditMessage={handleEditMessage}
            onDeleteMessage={handleDeleteMessage}
          />
        ) : (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '100%',
            color: 'text.secondary'
          }}>
            <ChatIcon sx={{ fontSize: 64, mb: 2, color: 'text.disabled' }} />
            <Typography variant="h6" color="text.secondary">
              채널을 선택하세요
            </Typography>
            <Typography variant="body2" color="text.disabled">
              좌측 목록에서 채널을 선택하거나 새 채널을 만들어보세요
            </Typography>
          </Box>
        )}
      </Box>

      {/* 채널 목록 사이드바 (오른쪽) */}
      {showChannelList && !isMobile && (
        <ChatChannelList
          key={channelListKey}
          selectedRoomId={chatRoomUuid}
          onSelectRoom={handleSelectRoom}
          onCreateRoom={handleCreateRoom}
          onOpenSettings={handleOpenSettings}
        />
      )}

      {isMobile && (
        <Drawer
          anchor="right"
          open={mobileChannelListOpen}
          onClose={() => setMobileChannelListOpen(false)}
          PaperProps={{
            sx: {
              width: 'min(320px, 88vw)',
              maxWidth: '88vw',
              overflow: 'hidden',
            },
          }}
        >
          <ChatChannelList
            key={`mobile-${channelListKey}`}
            selectedRoomId={chatRoomUuid}
            onSelectRoom={handleSelectRoom}
            onCreateRoom={handleCreateRoom}
            onOpenSettings={handleOpenSettings}
            mobile
            onSelectComplete={() => setMobileChannelListOpen(false)}
          />
        </Drawer>
      )}

      {/* 채널 생성 다이얼로그 */}
      <CreateChannelDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={handleChannelCreated}
      />

      {/* 채널 설정 다이얼로그 */}
      <ChannelSettingsDialog
        open={settingsDialogOpen}
        onClose={() => {
          setSettingsDialogOpen(false);
          setSelectedChannelForSettings(null);
        }}
        chatRoom={selectedChannelForSettings}
        onUpdated={handleChannelUpdated}
      />

      {/* 멤버 추가 다이얼로그 (채널 설정 재활용) */}
      <ChannelSettingsDialog
        open={addMemberDialogOpen}
        onClose={() => {
          setAddMemberDialogOpen(false);
          reloadParticipants(effectiveChatRoomUuid);
        }}
        chatRoom={participants.find(p => p.chatRoomUuid === effectiveChatRoomUuid)
          ? { chatRoomUuid: effectiveChatRoomUuid, name: selectedChannelName }
          : { chatRoomUuid: effectiveChatRoomUuid, name: selectedChannelName }}
        onUpdated={() => reloadParticipants(effectiveChatRoomUuid)}
        defaultTab="members"
      />

      {/* 디버그 정보 패널 (개발 모드에서만 표시) */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{
          position: 'fixed',
          top: 10,
          right: 10,
          background: 'rgba(0,0,0,0.8)',
          color: 'white',
          borderRadius: '5px',
          fontSize: '12px',
          zIndex: 9999,
          fontFamily: 'monospace',
          maxWidth: debugPanelOpen ? '300px' : '120px',
          transition: 'all 0.3s ease'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px',
            borderBottom: debugPanelOpen ? '1px solid rgba(255,255,255,0.2)' : 'none'
          }}>
            <div style={{ fontWeight: 'bold' }}>🔧 디버그</div>
            <button 
              onClick={() => setDebugPanelOpen(!debugPanelOpen)}
              style={{ 
                fontSize: '10px', 
                background: 'transparent',
                border: '1px solid white',
                color: 'white',
                padding: '2px 6px',
                borderRadius: '3px',
                cursor: 'pointer'
              }}
            >
              {debugPanelOpen ? '▲' : '▼'}
            </button>
          </div>
          
          {debugPanelOpen && (
            <div style={{ padding: '10px' }}>
              <div>🔗 연결상태: {connectionStatus}</div>
              <div>📡 STOMP연결: {stompClient?.connected ? '✅' : '❌'}</div>
              <div>🔄 활성상태: {stompClient?.active ? '✅' : '❌'}</div>
              <div>📊 재연결횟수: {connectionDebugInfo.reconnectAttempts}</div>
              <div>⏰ 마지막변경: {connectionDebugInfo.lastStatusChange || 'N/A'}</div>
              
              <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <button 
                  onClick={() => {
                    if (stompClient?.connected) {
                      logConnectionStatus(CONNECTION_STATUS.CONNECTED, '- 수동 연결 확인');
                    } else {
                      logConnectionStatus(CONNECTION_STATUS.DISCONNECTED, '- 수동 연결 실패 확인');
                    }
                  }}
                  style={{ fontSize: '10px', padding: '4px', cursor: 'pointer' }}
                >
                  연결테스트
                </button>
                <button 
                  onClick={() => logConnectionStatus(CONNECTION_STATUS.CONNECTED, '- 수동 연결됨 테스트')}
                  style={{ fontSize: '10px', padding: '4px', cursor: 'pointer' }}
                >
                  연결됨시뮬레이션
                </button>
                <button 
                  onClick={() => logConnectionStatus(CONNECTION_STATUS.ERROR, '- 수동 오류 테스트')}
                  style={{ fontSize: '10px', padding: '4px', cursor: 'pointer' }}
                >
                  오류시뮬레이션
                </button>
                <button 
                  onClick={() => {
                    logConnectionStatus(CONNECTION_STATUS.RECONNECTING, '- 수동 재연결 테스트');
                    setTimeout(() => logConnectionStatus(CONNECTION_STATUS.CONNECTED, '- 재연결 완료'), 3000);
                  }}
                  style={{ fontSize: '10px', padding: '4px', cursor: 'pointer' }}
                >
                  재연결시뮬레이션
                </button>
                <button 
                  onClick={() => actualSendMessage('테스트 메시지')}
                  style={{ fontSize: '10px', padding: '4px', cursor: 'pointer' }}
                >
                  메시지테스트
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </Box>
  );
};

export default MainchatContainer;
